'use client';

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

interface FeatureManagerProps {
  initialFeatures: Record<string, Record<string, boolean>>;
  sectionConfig: Record<string, any>;
  resetTrigger?: boolean;
}

/**
 * Custom hook to manage per-section features and multi-select states.
 * Supports toggling individual features, resetting features, and handling external reset triggers.
 *
 * @param initialFeatures - Initial feature state by section and feature keys.
 * @param sectionConfig - Configuration object for sections (e.g., whether they support features).
 * @param resetTrigger - Optional boolean trigger to externally reset all features.
 */
export const useFeatureManager = (
  initialFeatures: Record<string, Record<string, boolean>>,
  sectionConfig: Record<string, any>,
  resetTrigger?: boolean
) => {
  const [activeFeatures, setActiveFeatures] = useState<Record<string, Record<string, boolean>>>(initialFeatures);
  const [sectionMultiSelectStates, setSectionMultiSelectStates] = useState<Record<string, boolean>>({});
  const lastResetTriggerRef = useRef<boolean>(false);
  const isFeatureChangingRef = useRef<boolean>(false);

  /**
   * Determines if the section has features that allow features.
   */
  const sectionHasFeatures = useCallback(
    (sectionKey: string): boolean => {
      return sectionConfig[sectionKey]?.hasFeatures || false;
    },
    [sectionConfig]
  );

  /**
   * Returns whether a section is in multi-select mode.
   */
  const getSectionMultiSelect = useCallback(
    (sectionKey: string): boolean => {
      return sectionMultiSelectStates[sectionKey] || false;
    },
    [sectionMultiSelectStates]
  );

  /**
   * Toggles multi-select mode for a specific section.
   */
  const toggleSectionMultiSelect = useCallback((sectionKey: string) => {
    setSectionMultiSelectStates((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  /**
   * Updates a single feature's active state.
   * Respects multi-select mode and skips updates if the section has no features.
   */
  const handleFeatureChange = useCallback(
    (featureKey: string, isActive: boolean, sectionKey: string) => {
      if (!sectionHasFeatures(sectionKey)) return;

      isFeatureChangingRef.current = true;
      const isMultiSelect = getSectionMultiSelect(sectionKey);

      setActiveFeatures((prev) => {
        const sectionFeatures = prev[sectionKey] || {};

        const newSectionFeatures = isMultiSelect
          ? { ...sectionFeatures, [featureKey]: isActive }
          : Object.keys(sectionFeatures).reduce((acc, key) => {
              acc[key] = key === featureKey ? isActive : false;
              return acc;
            }, {} as Record<string, boolean>);

        const newState = {
          ...prev,
          [sectionKey]: newSectionFeatures,
        };

        setTimeout(() => {
          isFeatureChangingRef.current = false;
        }, 100);

        return newState;
      });
    },
    [sectionHasFeatures, getSectionMultiSelect]
  );

  /**
   * Resets all features to a given state across all sections.
   * Ignores reset if a feature change is currently in progress.
   */
  const resetAllFeatures = useCallback(
    (resetToFeatures: Record<string, Record<string, boolean>>) => {
      if (isFeatureChangingRef.current) {
        return;
      }

      setActiveFeatures(resetToFeatures);

      const resetMultiSelectStates: Record<string, boolean> = {};
      Object.keys(sectionConfig).forEach((sectionKey) => {
        resetMultiSelectStates[sectionKey] = false;
      });

      setSectionMultiSelectStates(resetMultiSelectStates);
    },
    [sectionConfig]
  );

  /**
   * Toggles a specific feature's value for a section.
   */
  const toggleFeature = useCallback((sectionKey: string, featureId: string) => {
    isFeatureChangingRef.current = true;

    setActiveFeatures((prev) => {
      const sectionFeatures = prev[sectionKey] || {};
      const newSectionFeatures = {
        ...sectionFeatures,
        [featureId]: !sectionFeatures[featureId],
      };

      setTimeout(() => {
        isFeatureChangingRef.current = false;
      }, 100);

      return {
        ...prev,
        [sectionKey]: newSectionFeatures,
      };
    });
  }, []);

  /**
   * Resets features for a specific section to its initial state.
   */
  const resetFeatures = useCallback(
    (sectionKey: string) => {
      if (isFeatureChangingRef.current) {
        return;
      }

      const initialSectionFeatures = initialFeatures[sectionKey] || {};

      setActiveFeatures((prev) => ({
        ...prev,
        [sectionKey]: initialSectionFeatures,
      }));

      setSectionMultiSelectStates((prev) => ({
        ...prev,
        [sectionKey]: false,
      }));
    },
    [initialFeatures]
  );

  /**
   * Clears all features in a section (sets them to false).
   */
  const clearAllFeatures = useCallback(
    (sectionKey: string) => {
      isFeatureChangingRef.current = true;

      const sectionFeatures = activeFeatures[sectionKey] || {};
      const clearedFeatures = Object.keys(sectionFeatures).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      setActiveFeatures((prev) => {
        setTimeout(() => {
          isFeatureChangingRef.current = false;
        }, 100);

        return {
          ...prev,
          [sectionKey]: clearedFeatures,
        };
      });
    },
    [activeFeatures]
  );

  /**
   * Resets section features using a memoized effect when resetTrigger changes.
   */
  useEffect(() => {
    if (resetTrigger !== lastResetTriggerRef.current) {
      if (resetTrigger && !isFeatureChangingRef.current) {
        resetAllFeatures(initialFeatures);
        lastResetTriggerRef.current = true;
      } else if (!resetTrigger && lastResetTriggerRef.current) {
        lastResetTriggerRef.current = false;
      } else if (resetTrigger && isFeatureChangingRef.current) {
      }
    }
  }, [resetTrigger, resetAllFeatures, initialFeatures]);

  /**
   * Alias for resetting a specific section's features.
   */
  const resetSectionFeatures = useCallback(
    (sectionKey: string) => {
      if (isFeatureChangingRef.current) {
        return;
      }

      const initialSectionFeatures = initialFeatures[sectionKey] || {};

      setActiveFeatures((prev) => ({
        ...prev,
        [sectionKey]: initialSectionFeatures,
      }));

      setSectionMultiSelectStates((prev) => ({
        ...prev,
        [sectionKey]: false,
      }));
    },
    [initialFeatures]
  );

  /**
   * Returns the number of active features for a section.
   */
  const getActiveFeatureCount = useCallback(
    (sectionKey: string): number => {
      const sectionFeatures = activeFeatures[sectionKey] || {};
      return Object.values(sectionFeatures).filter(Boolean).length;
    },
    [activeFeatures]
  );

  /**
   * Returns a list of active feature keys for a section.
   */
  const getActiveFeaturesForSection = useCallback(
    (sectionKey: string): string[] => {
      const sectionFeatures = activeFeatures[sectionKey] || {};
      return Object.keys(sectionFeatures).filter((key) => sectionFeatures[key]);
    },
    [activeFeatures]
  );

  /**
   * Returns true if any features are active in a section.
   */
  const hasActiveFeatures = useCallback(
    (sectionKey: string): boolean => {
      return getActiveFeatureCount(sectionKey) > 0;
    },
    [getActiveFeatureCount]
  );

  /**
   * Computed values for sections with features and total active features.
   */
  const computedValues = useMemo(() => {
    const sectionsWithFeatures = Object.keys(sectionConfig).filter((key) => sectionHasFeatures(key));
    const sectionsWithActiveFeatures = sectionsWithFeatures.filter((key) => hasActiveFeatures(key));
    const totalActiveFeatures = sectionsWithFeatures.reduce((total, key) => total + getActiveFeatureCount(key), 0);

    return {
      sectionsWithFeatures,
      sectionsWithActiveFeatures,
      totalActiveFeatures,
    };
  }, [sectionConfig, sectionHasFeatures, hasActiveFeatures, getActiveFeatureCount]);

  return {
    activeFeatures,
    sectionMultiSelectStates,
    handleFeatureChange,
    toggleSectionMultiSelect,
    resetAllFeatures,
    resetSectionFeatures,
    getSectionMultiSelect,
    sectionHasFeatures,
    getActiveFeatureCount,
    getActiveFeaturesForSection,
    hasActiveFeatures,
    toggleFeature,
    resetFeatures,
    clearAllFeatures,
    ...computedValues,
  };
};